export type Language = 'es' | 'en' | 'fr' | 'zh' | 'pt' | 'de'

export interface LanguageOption {
  code: Language
  name: string
  flag: string
  nativeName: string
}

export const LANGUAGES: LanguageOption[] = [
  { code: 'es', name: 'Spanish', flag: '🇪🇸', nativeName: 'Español' },
  { code: 'en', name: 'English', flag: '🇺🇸', nativeName: 'English' },
  { code: 'fr', name: 'French', flag: '🇫🇷', nativeName: 'Français' },
  { code: 'zh', name: 'Chinese', flag: '🇨🇳', nativeName: '中文' },
  { code: 'pt', name: 'Portuguese', flag: '🇧🇷', nativeName: 'Português' },
  { code: 'de', name: 'German', flag: '🇩🇪', nativeName: 'Deutsch' },
]

// CRC to USD exchange rate (approximate)
export const CRC_TO_USD_RATE = 512

// ─── Currency System ───────────────────────────────────────────────

export interface CurrencyInfo {
  code: string
  symbol: string
  name: string
  rateFromUSD: number
  position: 'before' | 'after'
  decimals: number
}

export const CURRENCIES: Record<string, CurrencyInfo> = {
  USD: {
    code: 'USD',
    symbol: '$',
    name: 'US Dollar',
    rateFromUSD: 1,
    position: 'before',
    decimals: 2,
  },
  CRC: {
    code: 'CRC',
    symbol: '₡',
    name: 'Colón',
    rateFromUSD: 512,
    position: 'before',
    decimals: 0,
  },
  BRL: {
    code: 'BRL',
    symbol: 'R$',
    name: 'Real Brasileiro',
    rateFromUSD: 5.05,
    position: 'before',
    decimals: 2,
  },
  EUR: {
    code: 'EUR',
    symbol: '€',
    name: 'Euro',
    rateFromUSD: 0.92,
    position: 'after',
    decimals: 2,
  },
  CNY: {
    code: 'CNY',
    symbol: '¥',
    name: 'Yuan',
    rateFromUSD: 7.25,
    position: 'before',
    decimals: 2,
  },
  GBP: {
    code: 'GBP',
    symbol: '£',
    name: 'Pound Sterling',
    rateFromUSD: 0.79,
    position: 'before',
    decimals: 2,
  },
  MXN: {
    code: 'MXN',
    symbol: 'MX$',
    name: 'Peso Mexicano',
    rateFromUSD: 17.5,
    position: 'before',
    decimals: 2,
  },
  JPY: {
    code: 'JPY',
    symbol: '¥',
    name: 'Yen',
    rateFromUSD: 150,
    position: 'before',
    decimals: 0,
  },
  CAD: {
    code: 'CAD',
    symbol: 'CA$',
    name: 'Canadian Dollar',
    rateFromUSD: 1.36,
    position: 'before',
    decimals: 2,
  },
  ARS: {
    code: 'ARS',
    symbol: 'AR$',
    name: 'Peso Argentino',
    rateFromUSD: 875,
    position: 'before',
    decimals: 0,
  },
  COP: {
    code: 'COP',
    symbol: 'COL$',
    name: 'Peso Colombiano',
    rateFromUSD: 3950,
    position: 'before',
    decimals: 0,
  },
  CLP: {
    code: 'CLP',
    symbol: 'CL$',
    name: 'Peso Chileno',
    rateFromUSD: 940,
    position: 'before',
    decimals: 0,
  },
  PEN: {
    code: 'PEN',
    symbol: 'S/',
    name: 'Sol Peruano',
    rateFromUSD: 3.72,
    position: 'before',
    decimals: 2,
  },
  KRW: {
    code: 'KRW',
    symbol: '₩',
    name: 'Won',
    rateFromUSD: 1330,
    position: 'before',
    decimals: 0,
  },
  INR: {
    code: 'INR',
    symbol: '₹',
    name: 'Indian Rupee',
    rateFromUSD: 83.5,
    position: 'before',
    decimals: 2,
  },
  AUD: {
    code: 'AUD',
    symbol: 'A$',
    name: 'Australian Dollar',
    rateFromUSD: 1.53,
    position: 'before',
    decimals: 2,
  },
  CHF: {
    code: 'CHF',
    symbol: 'CHF ',
    name: 'Swiss Franc',
    rateFromUSD: 0.88,
    position: 'before',
    decimals: 2,
  },
  SEK: {
    code: 'SEK',
    symbol: ' kr',
    name: 'Swedish Krona',
    rateFromUSD: 10.5,
    position: 'after',
    decimals: 2,
  },
  NZD: {
    code: 'NZD',
    symbol: 'NZ$',
    name: 'NZ Dollar',
    rateFromUSD: 1.67,
    position: 'before',
    decimals: 2,
  },
  ILS: {
    code: 'ILS',
    symbol: '₪',
    name: 'Shekel',
    rateFromUSD: 3.65,
    position: 'before',
    decimals: 2,
  },
  PAB: {
    code: 'PAB',
    symbol: 'B/',
    name: 'Balboa',
    rateFromUSD: 1,
    position: 'before',
    decimals: 2,
  },
  GTQ: {
    code: 'GTQ',
    symbol: 'Q',
    name: 'Quetzal',
    rateFromUSD: 7.85,
    position: 'before',
    decimals: 2,
  },
  HNL: {
    code: 'HNL',
    symbol: 'L',
    name: 'Lempira',
    rateFromUSD: 24.7,
    position: 'before',
    decimals: 2,
  },
  NIO: {
    code: 'NIO',
    symbol: 'C$',
    name: 'Córdoba',
    rateFromUSD: 36.7,
    position: 'before',
    decimals: 2,
  },
  DOP: {
    code: 'DOP',
    symbol: 'RD$',
    name: 'Peso Dominicano',
    rateFromUSD: 58.5,
    position: 'before',
    decimals: 2,
  },
  TWD: {
    code: 'TWD',
    symbol: 'NT$',
    name: 'Taiwan Dollar',
    rateFromUSD: 31.5,
    position: 'before',
    decimals: 0,
  },
  THB: {
    code: 'THB',
    symbol: '฿',
    name: 'Thai Baht',
    rateFromUSD: 35.5,
    position: 'before',
    decimals: 2,
  },
  PHP: {
    code: 'PHP',
    symbol: '₱',
    name: 'Philippine Peso',
    rateFromUSD: 56.5,
    position: 'before',
    decimals: 2,
  },
  ZAR: {
    code: 'ZAR',
    symbol: 'R',
    name: 'South African Rand',
    rateFromUSD: 18.5,
    position: 'before',
    decimals: 2,
  },
  PLN: {
    code: 'PLN',
    symbol: 'zł',
    name: 'Polish Zloty',
    rateFromUSD: 4.05,
    position: 'after',
    decimals: 2,
  },
  NOK: {
    code: 'NOK',
    symbol: ' kr',
    name: 'Norwegian Krone',
    rateFromUSD: 10.8,
    position: 'after',
    decimals: 2,
  },
  DKK: {
    code: 'DKK',
    symbol: ' kr',
    name: 'Danish Krone',
    rateFromUSD: 6.88,
    position: 'after',
    decimals: 2,
  },
}

// Map ISO country codes to currency codes
export const COUNTRY_TO_CURRENCY: Record<string, string> = {
  // Americas
  CR: 'CRC',
  US: 'USD',
  BR: 'BRL',
  MX: 'MXN',
  CA: 'CAD',
  AR: 'ARS',
  CO: 'COP',
  CL: 'CLP',
  PE: 'PEN',
  PA: 'PAB',
  GT: 'GTQ',
  HN: 'HNL',
  NI: 'NIO',
  DO: 'DOP',
  SV: 'USD',
  EC: 'USD',
  PR: 'USD',
  // Europe
  FR: 'EUR',
  DE: 'EUR',
  ES: 'EUR',
  PT: 'EUR',
  IT: 'EUR',
  NL: 'EUR',
  BE: 'EUR',
  AT: 'EUR',
  IE: 'EUR',
  FI: 'EUR',
  GR: 'EUR',
  SK: 'EUR',
  SI: 'EUR',
  LT: 'EUR',
  LV: 'EUR',
  EE: 'EUR',
  CY: 'EUR',
  MT: 'EUR',
  LU: 'EUR',
  HR: 'EUR',
  GB: 'GBP',
  CH: 'CHF',
  SE: 'SEK',
  NO: 'NOK',
  DK: 'DKK',
  PL: 'PLN',
  // Asia & Pacific
  CN: 'CNY',
  JP: 'JPY',
  KR: 'KRW',
  IN: 'INR',
  AU: 'AUD',
  NZ: 'NZD',
  IL: 'ILS',
  TW: 'TWD',
  TH: 'THB',
  PH: 'PHP',
  // Africa
  ZA: 'ZAR',
}

// ─── Translations ──────────────────────────────────────────────────

type TranslationKeys = {
  tagline: string
  orderNow: string
  all: string
  whatDoYouCrave: string
  sandwich: string
  wrap: string
  soups: string
  snackBowl: string
  kids: string
  drinks: string
  customize: string
  add: string
  yourOrder: string
  viewOrder: string
  emptyCart: string
  subtotal: string
  continueCheckout: string
  personalize: string
  toppings: string
  dressing: string
  optional: string
  comments: string
  commentsPlaceholder: string
  addToOrder: string
  chooseMin1: string
  maxReached: string
  tapToSwap: string
  chooseToppings: string
  chooseDressing: string
  specialInstructions: string
  yourIngredients: string
  beverages: string
  beverageOptional: string
  addBeverage: string
  options: string
  remove: string
  buildYourBowl: string
  step: string
  of: string
  required: string
  selected: string
  skip: string
  next: string
  yourBowl: string
  ingredientsSelected: string
  included: string
  finishOrder: string
  yourData: string
  dataNeeded: string
  fullName: string
  namePlaceholder: string
  phone: string
  phonePlaceholder: string
  phoneHint: string
  deliveryType: string
  deliveryChoose: string
  pickupLocal: string
  express: string
  pickupHint: string
  expressCharge: string
  exactAddress: string
  addressOptional: string
  addressPlaceholder: string
  mapLocation: string
  mapRecommended: string
  paymentMethod: string
  paymentChoose: string
  card: string
  cash: string
  sinpeHint: string
  cardHint: string
  payWith: string
  payWithOptional: string
  payWithPlaceholder: string
  estimatedChange: string
  changeHint: string
  additionalComments: string
  noCommentsHint: string
  allergies: string
  allergiesPlaceholder: string
  allergiesWarning: string
  restaurantNotes: string
  restaurantNotesPlaceholder: string
  totalToPay: string
  generatingTicket: string
  sendWhatsApp: string
  completeName: string
  addPhone: string
  addProduct: string
  addAddressOrLocation: string
  save: string
  sendViaWhatsApp: string
  moveMapHint: string
  confirmLocation: string
  saved: string
  searchingLocation: string
  orderNumber: string
  client: string
  tel: string
  delivery: string
  payment: string
  qty: string
  description: string
  total: string
  thankYou: string
  followUs: string
  usd: string
  edit: string
  note: string
  addSomethingMore: string
}

export const translations: Record<Language, TranslationKeys> = {
  es: {
    tagline: 'Donde Los Santos viene a comer 🔥',
    orderNow: 'Hacé tu pedido',
    all: 'Todos',
    whatDoYouCrave: '¿Qué se te antoja?',
    sandwich: 'Sándwich',
    wrap: 'Wrap',
    soups: 'Cremas',
    snackBowl: 'Snack Bowl',
    kids: 'Niños',
    drinks: 'Bebidas',
    customize: 'Personalizar',
    add: 'Agregar',
    yourOrder: 'Tu Pedido',
    viewOrder: 'Ver pedido',
    emptyCart: 'Tu carrito está vacío',
    subtotal: 'Subtotal',
    continueCheckout: 'Continuar al Checkout',
    personalize: 'Personalizar',
    toppings: 'Toppings',
    dressing: 'Aderezo',
    optional: 'Opcional',
    comments: 'Comentarios',
    commentsPlaceholder: 'Ej: sin cebolla, extra salsa, etc.',
    addToOrder: 'Agregar al pedido',
    chooseMin1: 'Elegí al menos 1 topping para continuar',
    maxReached: '¡Máximo alcanzado! Tocá uno para cambiarlo',
    tapToSwap: 'Tocá uno para cambiarlo',
    chooseToppings: 'Elegí de 1 a 4 toppings — mínimo 1 para continuar',
    chooseDressing: 'Elegí 1 aderezo o dejalo vacío si preferís sin aderezo',
    specialInstructions:
      '¿Alguna instrucción especial? Si no, simplemente agregá al pedido',
    yourIngredients: 'Tus ingredientes aparecerán aquí...',
    beverages: 'Bebidas',
    beverageOptional: 'Opcional · Agregá una bebida a tu combo',
    addBeverage: 'Agregá una bebida',
    options: 'opciones',
    remove: 'Quitar',
    buildYourBowl: 'Armá tu Bowl',
    step: 'Paso',
    of: 'de',
    required: 'Requerido',
    selected: 'Seleccionados',
    skip: 'Saltar',
    next: 'Siguiente',
    yourBowl: 'Tu Bowl',
    ingredientsSelected: 'ingredientes seleccionados',
    included: 'Incluido',
    finishOrder: 'Finalizar Pedido',
    yourData: 'Tus Datos',
    dataNeeded: 'Necesitamos estos datos para contactarte sobre tu pedido',
    fullName: 'Nombre completo',
    namePlaceholder: 'Ej. María Pérez',
    phone: 'Teléfono',
    phonePlaceholder: 'Ej. 88888888',
    phoneHint: 'Para coordinación de entrega o dudas sobre tu pedido',
    deliveryType: 'Tipo de Entrega',
    deliveryChoose: 'Elegí cómo querés recibir tu pedido',
    pickupLocal: 'Retiro en local',
    express: 'Express',
    pickupHint:
      'Pasá a recoger tu pedido cuando esté listo. Te avisaremos por WhatsApp.',
    expressCharge: 'El servicio express tiene un cargo adicional de',
    exactAddress: 'Dirección exacta',
    addressOptional: '(opcional si compartís ubicación)',
    addressPlaceholder: 'Ej: San José, Escazú, frente al parque central...',
    mapLocation: 'Ubicación en el mapa',
    mapRecommended: '(recomendado para mayor precisión)',
    paymentMethod: 'Método de Pago',
    paymentChoose: '¿Cómo vas a pagar tu pedido?',
    card: 'Tarjeta',
    cash: 'Efectivo',
    sinpeHint:
      '📱 Te enviaremos el número de SINPE por WhatsApp para que realices la transferencia',
    cardHint:
      '💳 Coordinamos el pago con tarjeta al momento de la entrega o retiro',
    payWith: '¿Con cuánto pagás?',
    payWithOptional: '(opcional)',
    payWithPlaceholder: 'Ej. 10000 — para calcular tu vuelto',
    estimatedChange: 'Vuelto estimado:',
    changeHint: 'Así preparamos tu vuelto exacto',
    additionalComments: 'Comentarios Adicionales',
    noCommentsHint:
      'Si no tenés nada que agregar, podés enviar tu pedido directamente',
    allergies: 'Alergias o restricciones alimentarias',
    allergiesPlaceholder: 'Ej: Alérgico al maní, intolerante a la lactosa...',
    allergiesWarning:
      '⚠️ Importante si tenés alguna alergia — dejalo vacío si no aplica',
    restaurantNotes: 'Notas para el restaurante',
    restaurantNotesPlaceholder:
      'Ej: Llegar por la puerta trasera, timbre no funciona, etc.',
    totalToPay: 'Total a pagar',
    generatingTicket: 'Generando ticket...',
    sendWhatsApp: 'Enviar pedido por WhatsApp',
    completeName: '• Completá tu nombre',
    addPhone: '• Agregá tu teléfono',
    addProduct: '• Agregá al menos un producto',
    addAddressOrLocation: '• Agregá dirección o ubicación en el mapa',
    save: 'Guardar',
    sendViaWhatsApp: 'Enviar por WhatsApp',
    moveMapHint:
      'Mové el mapa para ajustar el pin rojo en tu ubicación exacta.',
    confirmLocation: 'Confirmar ubicación',
    saved: 'Guardada',
    searchingLocation: 'Buscando tu ubicación...',
    orderNumber: 'PEDIDO',
    client: 'Cliente',
    tel: 'Tel',
    delivery: 'Entrega',
    payment: 'Pago',
    qty: 'CANT',
    description: 'DESCRIPCIÓN',
    total: 'TOTAL',
    thankYou: '¡GRACIAS POR TU COMPRA!',
    followUs: 'Síguenos en redes sociales',
    usd: 'USD',
    edit: 'Editar',
    note: 'Nota',
    addSomethingMore: 'Agregar algo más',
  },
  en: {
    tagline: 'Where Los Santos comes to eat 🔥',
    orderNow: 'Order Now',
    all: 'All',
    whatDoYouCrave: 'What are you craving?',
    sandwich: 'Sandwich',
    wrap: 'Wrap',
    soups: 'Soups',
    snackBowl: 'Snack Bowl',
    kids: 'Kids',
    drinks: 'Drinks',
    customize: 'Customize',
    add: 'Add',
    yourOrder: 'Your Order',
    viewOrder: 'View order',
    emptyCart: 'Your cart is empty',
    subtotal: 'Subtotal',
    continueCheckout: 'Continue to Checkout',
    personalize: 'Customize',
    toppings: 'Toppings',
    dressing: 'Dressing',
    optional: 'Optional',
    comments: 'Comments',
    commentsPlaceholder: 'E.g.: no onion, extra sauce, etc.',
    addToOrder: 'Add to order',
    chooseMin1: 'Choose at least 1 topping to continue',
    maxReached: 'Max reached! Tap one to swap it',
    tapToSwap: 'Tap one to swap it',
    chooseToppings: 'Choose 1 to 4 toppings — minimum 1 to continue',
    chooseDressing: 'Choose 1 dressing or leave empty for no dressing',
    specialInstructions: 'Any special instructions? If not, just add to order',
    yourIngredients: 'Your ingredients will appear here...',
    beverages: 'Beverages',
    beverageOptional: 'Optional · Add a drink to your combo',
    addBeverage: 'Add a drink',
    options: 'options',
    remove: 'Remove',
    buildYourBowl: 'Build Your Bowl',
    step: 'Step',
    of: 'of',
    required: 'Required',
    selected: 'Selected',
    skip: 'Skip',
    next: 'Next',
    yourBowl: 'Your Bowl',
    ingredientsSelected: 'ingredients selected',
    included: 'Included',
    finishOrder: 'Finish Order',
    yourData: 'Your Info',
    dataNeeded: 'We need this info to contact you about your order',
    fullName: 'Full name',
    namePlaceholder: 'E.g. John Doe',
    phone: 'Phone',
    phonePlaceholder: 'E.g. 88888888',
    phoneHint: 'For delivery coordination or questions about your order',
    deliveryType: 'Delivery Type',
    deliveryChoose: 'Choose how you want to receive your order',
    pickupLocal: 'Pickup',
    express: 'Express',
    pickupHint: "Pick up your order when ready. We'll notify you via WhatsApp.",
    expressCharge: 'Express delivery has an additional charge of',
    exactAddress: 'Exact address',
    addressOptional: '(optional if you share location)',
    addressPlaceholder:
      'E.g.: San José, Escazú, in front of the central park...',
    mapLocation: 'Map location',
    mapRecommended: '(recommended for better accuracy)',
    paymentMethod: 'Payment Method',
    paymentChoose: 'How will you pay?',
    card: 'Card',
    cash: 'Cash',
    sinpeHint:
      "📱 We'll send you the SINPE number via WhatsApp for the transfer",
    cardHint: "💳 We'll coordinate card payment at delivery or pickup",
    payWith: 'Paying with?',
    payWithOptional: '(optional)',
    payWithPlaceholder: 'E.g. 10000 — to calculate your change',
    estimatedChange: 'Estimated change:',
    changeHint: 'So we prepare your exact change',
    additionalComments: 'Additional Comments',
    noCommentsHint: 'If nothing to add, you can send your order directly',
    allergies: 'Allergies or dietary restrictions',
    allergiesPlaceholder: 'E.g.: Allergic to peanuts, lactose intolerant...',
    allergiesWarning:
      '⚠️ Important if you have any allergies — leave empty if not applicable',
    restaurantNotes: 'Notes for the restaurant',
    restaurantNotesPlaceholder:
      "E.g.: Use the back door, doorbell doesn't work, etc.",
    totalToPay: 'Total to pay',
    generatingTicket: 'Generating ticket...',
    sendWhatsApp: 'Send order via WhatsApp',
    completeName: '• Complete your name',
    addPhone: '• Add your phone',
    addProduct: '• Add at least one product',
    addAddressOrLocation: '• Add address or map location',
    save: 'Save',
    sendViaWhatsApp: 'Send via WhatsApp',
    moveMapHint: 'Move the map to place the red pin on your exact location.',
    confirmLocation: 'Confirm location',
    saved: 'Saved',
    searchingLocation: 'Finding your location...',
    orderNumber: 'ORDER',
    client: 'Client',
    tel: 'Phone',
    delivery: 'Delivery',
    payment: 'Payment',
    qty: 'QTY',
    description: 'DESCRIPTION',
    total: 'TOTAL',
    thankYou: 'THANK YOU FOR YOUR ORDER!',
    followUs: 'Follow us on social media',
    usd: 'USD',
    edit: 'Edit',
    note: 'Note',
    addSomethingMore: 'Add something else',
  },
  fr: {
    tagline: 'Où Los Santos vient manger 🔥',
    orderNow: 'Commander',
    all: 'Tous',
    whatDoYouCrave: "Qu'est-ce qui vous tente ?",
    sandwich: 'Sandwich',
    wrap: 'Wrap',
    soups: 'Soupes',
    snackBowl: 'Snack Bowl',
    kids: 'Enfants',
    drinks: 'Boissons',
    customize: 'Personnaliser',
    add: 'Ajouter',
    yourOrder: 'Votre Commande',
    viewOrder: 'Voir commande',
    emptyCart: 'Votre panier est vide',
    subtotal: 'Sous-total',
    continueCheckout: 'Continuer',
    personalize: 'Personnaliser',
    toppings: 'Garnitures',
    dressing: 'Sauce',
    optional: 'Optionnel',
    comments: 'Commentaires',
    commentsPlaceholder: 'Ex : sans oignon, sauce extra, etc.',
    addToOrder: 'Ajouter à la commande',
    chooseMin1: 'Choisissez au moins 1 garniture',
    maxReached: 'Maximum atteint ! Touchez-en un pour le changer',
    tapToSwap: 'Touchez-en un pour le changer',
    chooseToppings: 'Choisissez 1 à 4 garnitures — minimum 1',
    chooseDressing: 'Choisissez 1 sauce ou laissez vide',
    specialInstructions: 'Instructions spéciales ? Sinon, ajoutez directement',
    yourIngredients: 'Vos ingrédients apparaîtront ici...',
    beverages: 'Boissons',
    beverageOptional: 'Optionnel · Ajoutez une boisson',
    addBeverage: 'Ajouter une boisson',
    options: 'options',
    remove: 'Retirer',
    buildYourBowl: 'Créez votre Bowl',
    step: 'Étape',
    of: 'de',
    required: 'Requis',
    selected: 'Sélectionnés',
    skip: 'Passer',
    next: 'Suivant',
    yourBowl: 'Votre Bowl',
    ingredientsSelected: 'ingrédients sélectionnés',
    included: 'Inclus',
    finishOrder: 'Finaliser la commande',
    yourData: 'Vos données',
    dataNeeded: 'Nous avons besoin de ces données pour vous contacter',
    fullName: 'Nom complet',
    namePlaceholder: 'Ex. Marie Dupont',
    phone: 'Téléphone',
    phonePlaceholder: 'Ex. 88888888',
    phoneHint: 'Pour la coordination de livraison',
    deliveryType: 'Type de livraison',
    deliveryChoose: 'Comment souhaitez-vous recevoir votre commande ?',
    pickupLocal: 'Retrait sur place',
    express: 'Express',
    pickupHint:
      'Récupérez votre commande quand elle est prête. Nous vous notifierons par WhatsApp.',
    expressCharge: 'La livraison express a un supplément de',
    exactAddress: 'Adresse exacte',
    addressOptional: '(optionnel si vous partagez votre position)',
    addressPlaceholder: 'Ex : San José, Escazú, en face du parc central...',
    mapLocation: 'Position sur la carte',
    mapRecommended: '(recommandé pour plus de précision)',
    paymentMethod: 'Mode de paiement',
    paymentChoose: 'Comment allez-vous payer ?',
    card: 'Carte',
    cash: 'Espèces',
    sinpeHint: '📱 Nous vous enverrons le numéro SINPE par WhatsApp',
    cardHint: '💳 Nous coordonnerons le paiement par carte à la livraison',
    payWith: 'Vous payez avec ?',
    payWithOptional: '(optionnel)',
    payWithPlaceholder: 'Ex. 10000 — pour calculer votre monnaie',
    estimatedChange: 'Monnaie estimée :',
    changeHint: 'Pour préparer votre monnaie exacte',
    additionalComments: 'Commentaires supplémentaires',
    noCommentsHint: 'Si rien à ajouter, envoyez directement votre commande',
    allergies: 'Allergies ou restrictions alimentaires',
    allergiesPlaceholder:
      'Ex : Allergique aux arachides, intolérant au lactose...',
    allergiesWarning:
      '⚠️ Important si vous avez des allergies — laissez vide sinon',
    restaurantNotes: 'Notes pour le restaurant',
    restaurantNotesPlaceholder:
      'Ex : Utilisez la porte arrière, la sonnette ne marche pas...',
    totalToPay: 'Total à payer',
    generatingTicket: 'Génération du ticket...',
    sendWhatsApp: 'Envoyer par WhatsApp',
    completeName: '• Complétez votre nom',
    addPhone: '• Ajoutez votre téléphone',
    addProduct: '• Ajoutez au moins un produit',
    addAddressOrLocation: '• Ajoutez adresse ou position',
    save: 'Enregistrer',
    sendViaWhatsApp: 'Envoyer par WhatsApp',
    moveMapHint:
      'Déplacez la carte pour placer le pin rouge sur votre position.',
    confirmLocation: 'Confirmer la position',
    saved: 'Enregistrée',
    searchingLocation: 'Recherche de votre position...',
    orderNumber: 'COMMANDE',
    client: 'Client',
    tel: 'Tél',
    delivery: 'Livraison',
    payment: 'Paiement',
    qty: 'QTÉ',
    description: 'DESCRIPTION',
    total: 'TOTAL',
    thankYou: 'MERCI POUR VOTRE COMMANDE !',
    followUs: 'Suivez-nous sur les réseaux',
    usd: 'USD',
    edit: 'Modifier',
    note: 'Note',
    addSomethingMore: 'Ajouter autre chose',
  },
  zh: {
    tagline: 'Los Santos 美食天堂 🔥',
    orderNow: '立即下单',
    all: '全部',
    whatDoYouCrave: '你想吃什么？',
    sandwich: '三明治',
    wrap: '卷饼',
    soups: '浓汤',
    snackBowl: '小食碗',
    kids: '儿童',
    drinks: '饮品',
    customize: '定制',
    add: '添加',
    yourOrder: '你的订单',
    viewOrder: '查看订单',
    emptyCart: '购物车为空',
    subtotal: '小计',
    continueCheckout: '继续结账',
    personalize: '定制',
    toppings: '配料',
    dressing: '酱料',
    optional: '可选',
    comments: '备注',
    commentsPlaceholder: '例：不要洋葱，多加酱料等',
    addToOrder: '加入订单',
    chooseMin1: '请至少选择1种配料',
    maxReached: '已达上限！点击一个来替换',
    tapToSwap: '点击一个来替换',
    chooseToppings: '选择1到4种配料 — 至少1种',
    chooseDressing: '选择1种酱料或留空',
    specialInstructions: '有特殊要求吗？没有的话直接添加',
    yourIngredients: '你的食材将显示在这里...',
    beverages: '饮品',
    beverageOptional: '可选 · 为你的套餐添加饮品',
    addBeverage: '添加饮品',
    options: '选项',
    remove: '移除',
    buildYourBowl: '定制你的碗',
    step: '步骤',
    of: '/',
    required: '必选',
    selected: '已选',
    skip: '跳过',
    next: '下一步',
    yourBowl: '你的碗',
    ingredientsSelected: '种食材已选',
    included: '已含',
    finishOrder: '完成订单',
    yourData: '你的信息',
    dataNeeded: '我们需要这些信息来联系你',
    fullName: '全名',
    namePlaceholder: '例：张三',
    phone: '电话',
    phonePlaceholder: '例：88888888',
    phoneHint: '用于配送协调或订单咨询',
    deliveryType: '配送方式',
    deliveryChoose: '选择你的配送方式',
    pickupLocal: '到店自取',
    express: '快递',
    pickupHint: '准备好后来取餐。我们会通过WhatsApp通知你。',
    expressCharge: '快递服务需额外收费',
    exactAddress: '详细地址',
    addressOptional: '（如分享位置则可选）',
    addressPlaceholder: '例：San José, Escazú, 中央公园对面...',
    mapLocation: '地图位置',
    mapRecommended: '（推荐，更精确）',
    paymentMethod: '支付方式',
    paymentChoose: '你将如何支付？',
    card: '银行卡',
    cash: '现金',
    sinpeHint: '📱 我们将通过WhatsApp发送SINPE号码',
    cardHint: '💳 我们将在配送或取餐时协调刷卡支付',
    payWith: '用多少支付？',
    payWithOptional: '（可选）',
    payWithPlaceholder: '例：10000 — 计算找零',
    estimatedChange: '预计找零：',
    changeHint: '以便准备零钱',
    additionalComments: '其他备注',
    noCommentsHint: '没有补充的话可以直接发送订单',
    allergies: '过敏或饮食限制',
    allergiesPlaceholder: '例：花生过敏，乳糖不耐受...',
    allergiesWarning: '⚠️ 如有过敏请务必填写 — 无则留空',
    restaurantNotes: '给餐厅的备注',
    restaurantNotesPlaceholder: '例：走后门，门铃坏了等',
    totalToPay: '应付总额',
    generatingTicket: '生成小票中...',
    sendWhatsApp: '通过WhatsApp发送订单',
    completeName: '• 请填写姓名',
    addPhone: '• 请添加电话',
    addProduct: '• 请至少添加一个产品',
    addAddressOrLocation: '• 请添加地址或地图位置',
    save: '保存',
    sendViaWhatsApp: '通过WhatsApp发送',
    moveMapHint: '移动地图将红色图钉放在你的确切位置。',
    confirmLocation: '确认位置',
    saved: '已保存',
    searchingLocation: '正在查找你的位置...',
    orderNumber: '订单',
    client: '客户',
    tel: '电话',
    delivery: '配送',
    payment: '支付',
    qty: '数量',
    description: '描述',
    total: '合计',
    thankYou: '感谢你的订单！',
    followUs: '关注我们的社交媒体',
    usd: 'USD',
    edit: '编辑',
    note: '备注',
    addSomethingMore: '添加其他',
  },
  pt: {
    tagline: 'Onde Los Santos vem comer 🔥',
    orderNow: 'Faça seu pedido',
    all: 'Todos',
    whatDoYouCrave: 'O que você quer comer?',
    sandwich: 'Sanduíche',
    wrap: 'Wrap',
    soups: 'Sopas',
    snackBowl: 'Snack Bowl',
    kids: 'Crianças',
    drinks: 'Bebidas',
    customize: 'Personalizar',
    add: 'Adicionar',
    yourOrder: 'Seu Pedido',
    viewOrder: 'Ver pedido',
    emptyCart: 'Seu carrinho está vazio',
    subtotal: 'Subtotal',
    continueCheckout: 'Continuar para o Checkout',
    personalize: 'Personalizar',
    toppings: 'Coberturas',
    dressing: 'Molho',
    optional: 'Opcional',
    comments: 'Comentários',
    commentsPlaceholder: 'Ex: sem cebola, molho extra, etc.',
    addToOrder: 'Adicionar ao pedido',
    chooseMin1: 'Escolha pelo menos 1 cobertura',
    maxReached: 'Máximo atingido! Toque em um para trocar',
    tapToSwap: 'Toque em um para trocar',
    chooseToppings: 'Escolha 1 a 4 coberturas — mínimo 1',
    chooseDressing: 'Escolha 1 molho ou deixe vazio',
    specialInstructions: 'Instruções especiais? Se não, apenas adicione',
    yourIngredients: 'Seus ingredientes aparecerão aqui...',
    beverages: 'Bebidas',
    beverageOptional: 'Opcional · Adicione uma bebida ao combo',
    addBeverage: 'Adicionar bebida',
    options: 'opções',
    remove: 'Remover',
    buildYourBowl: 'Monte seu Bowl',
    step: 'Passo',
    of: 'de',
    required: 'Obrigatório',
    selected: 'Selecionados',
    skip: 'Pular',
    next: 'Próximo',
    yourBowl: 'Seu Bowl',
    ingredientsSelected: 'ingredientes selecionados',
    included: 'Incluído',
    finishOrder: 'Finalizar Pedido',
    yourData: 'Seus Dados',
    dataNeeded: 'Precisamos desses dados para entrar em contato',
    fullName: 'Nome completo',
    namePlaceholder: 'Ex. Maria Silva',
    phone: 'Telefone',
    phonePlaceholder: 'Ex. 88888888',
    phoneHint: 'Para coordenação de entrega ou dúvidas',
    deliveryType: 'Tipo de Entrega',
    deliveryChoose: 'Como deseja receber seu pedido?',
    pickupLocal: 'Retirada no local',
    express: 'Express',
    pickupHint:
      'Retire seu pedido quando estiver pronto. Avisaremos por WhatsApp.',
    expressCharge: 'O serviço express tem uma taxa adicional de',
    exactAddress: 'Endereço exato',
    addressOptional: '(opcional se compartilhar localização)',
    addressPlaceholder: 'Ex: San José, Escazú, em frente ao parque central...',
    mapLocation: 'Localização no mapa',
    mapRecommended: '(recomendado para maior precisão)',
    paymentMethod: 'Método de Pagamento',
    paymentChoose: 'Como vai pagar?',
    card: 'Cartão',
    cash: 'Dinheiro',
    sinpeHint: '📱 Enviaremos o número SINPE por WhatsApp',
    cardHint: '💳 Coordenaremos o pagamento com cartão na entrega',
    payWith: 'Vai pagar com quanto?',
    payWithOptional: '(opcional)',
    payWithPlaceholder: 'Ex. 10000 — para calcular o troco',
    estimatedChange: 'Troco estimado:',
    changeHint: 'Para prepararmos o troco exato',
    additionalComments: 'Comentários Adicionais',
    noCommentsHint: 'Se não tem nada a acrescentar, envie diretamente',
    allergies: 'Alergias ou restrições alimentares',
    allergiesPlaceholder: 'Ex: Alérgico a amendoim, intolerante a lactose...',
    allergiesWarning:
      '⚠️ Importante se tiver alguma alergia — deixe vazio se não aplicável',
    restaurantNotes: 'Notas para o restaurante',
    restaurantNotesPlaceholder:
      'Ex: Usar a porta dos fundos, campainha não funciona...',
    totalToPay: 'Total a pagar',
    generatingTicket: 'Gerando ticket...',
    sendWhatsApp: 'Enviar pedido por WhatsApp',
    completeName: '• Complete seu nome',
    addPhone: '• Adicione seu telefone',
    addProduct: '• Adicione pelo menos um produto',
    addAddressOrLocation: '• Adicione endereço ou localização',
    save: 'Salvar',
    sendViaWhatsApp: 'Enviar por WhatsApp',
    moveMapHint: 'Mova o mapa para colocar o pin vermelho na sua localização.',
    confirmLocation: 'Confirmar localização',
    saved: 'Salva',
    searchingLocation: 'Buscando sua localização...',
    orderNumber: 'PEDIDO',
    client: 'Cliente',
    tel: 'Tel',
    delivery: 'Entrega',
    payment: 'Pagamento',
    qty: 'QTD',
    description: 'DESCRIÇÃO',
    total: 'TOTAL',
    thankYou: 'OBRIGADO PELO SEU PEDIDO!',
    followUs: 'Siga-nos nas redes sociais',
    usd: 'USD',
    edit: 'Editar',
    note: 'Nota',
    addSomethingMore: 'Adicionar algo mais',
  },
  de: {
    tagline: 'Wo Los Santos essen kommt 🔥',
    orderNow: 'Jetzt bestellen',
    all: 'Alle',
    whatDoYouCrave: 'Worauf hast du Lust?',
    sandwich: 'Sandwich',
    wrap: 'Wrap',
    soups: 'Suppen',
    snackBowl: 'Snack Bowl',
    kids: 'Kinder',
    drinks: 'Getränke',
    customize: 'Anpassen',
    add: 'Hinzufügen',
    yourOrder: 'Deine Bestellung',
    viewOrder: 'Bestellung ansehen',
    emptyCart: 'Dein Warenkorb ist leer',
    subtotal: 'Zwischensumme',
    continueCheckout: 'Weiter zur Kasse',
    personalize: 'Anpassen',
    toppings: 'Beläge',
    dressing: 'Dressing',
    optional: 'Optional',
    comments: 'Kommentare',
    commentsPlaceholder: 'Z.B.: keine Zwiebeln, extra Soße, etc.',
    addToOrder: 'Zur Bestellung hinzufügen',
    chooseMin1: 'Wähle mindestens 1 Belag',
    maxReached: 'Maximum erreicht! Tippe auf einen zum Tauschen',
    tapToSwap: 'Tippe auf einen zum Tauschen',
    chooseToppings: 'Wähle 1 bis 4 Beläge — mindestens 1',
    chooseDressing: 'Wähle 1 Dressing oder lasse es leer',
    specialInstructions: 'Besondere Wünsche? Wenn nicht, einfach hinzufügen',
    yourIngredients: 'Deine Zutaten erscheinen hier...',
    beverages: 'Getränke',
    beverageOptional: 'Optional · Füge ein Getränk hinzu',
    addBeverage: 'Getränk hinzufügen',
    options: 'Optionen',
    remove: 'Entfernen',
    buildYourBowl: 'Baue deine Bowl',
    step: 'Schritt',
    of: 'von',
    required: 'Erforderlich',
    selected: 'Ausgewählt',
    skip: 'Überspringen',
    next: 'Weiter',
    yourBowl: 'Deine Bowl',
    ingredientsSelected: 'Zutaten ausgewählt',
    included: 'Inklusive',
    finishOrder: 'Bestellung abschließen',
    yourData: 'Deine Daten',
    dataNeeded: 'Wir brauchen diese Daten um dich zu kontaktieren',
    fullName: 'Vollständiger Name',
    namePlaceholder: 'Z.B. Max Mustermann',
    phone: 'Telefon',
    phonePlaceholder: 'Z.B. 88888888',
    phoneHint: 'Für Lieferkoordination oder Fragen',
    deliveryType: 'Lieferart',
    deliveryChoose: 'Wie möchtest du deine Bestellung erhalten?',
    pickupLocal: 'Abholung',
    express: 'Express',
    pickupHint:
      'Hole deine Bestellung ab wenn sie fertig ist. Wir benachrichtigen dich per WhatsApp.',
    expressCharge: 'Der Express-Service hat einen Aufpreis von',
    exactAddress: 'Genaue Adresse',
    addressOptional: '(optional wenn Standort geteilt)',
    addressPlaceholder: 'Z.B.: San José, Escazú, gegenüber dem Zentralpark...',
    mapLocation: 'Kartenstandort',
    mapRecommended: '(empfohlen für bessere Genauigkeit)',
    paymentMethod: 'Zahlungsmethode',
    paymentChoose: 'Wie möchtest du bezahlen?',
    card: 'Karte',
    cash: 'Bargeld',
    sinpeHint: '📱 Wir senden dir die SINPE-Nummer per WhatsApp',
    cardHint: '💳 Kartenzahlung bei Lieferung oder Abholung',
    payWith: 'Womit bezahlst du?',
    payWithOptional: '(optional)',
    payWithPlaceholder: 'Z.B. 10000 — für Wechselgeld',
    estimatedChange: 'Geschätztes Wechselgeld:',
    changeHint: 'Damit wir das passende Wechselgeld vorbereiten',
    additionalComments: 'Zusätzliche Kommentare',
    noCommentsHint: 'Wenn nichts hinzuzufügen, sende direkt',
    allergies: 'Allergien oder Ernährungseinschränkungen',
    allergiesPlaceholder: 'Z.B.: Erdnussallergie, Laktoseintoleranz...',
    allergiesWarning:
      '⚠️ Wichtig bei Allergien — leer lassen wenn nicht zutreffend',
    restaurantNotes: 'Notizen für das Restaurant',
    restaurantNotesPlaceholder:
      'Z.B.: Hintertür benutzen, Klingel funktioniert nicht...',
    totalToPay: 'Gesamtbetrag',
    generatingTicket: 'Ticket wird erstellt...',
    sendWhatsApp: 'Bestellung per WhatsApp senden',
    completeName: '• Name vervollständigen',
    addPhone: '• Telefon hinzufügen',
    addProduct: '• Mindestens ein Produkt hinzufügen',
    addAddressOrLocation: '• Adresse oder Standort hinzufügen',
    save: 'Speichern',
    sendViaWhatsApp: 'Per WhatsApp senden',
    moveMapHint:
      'Bewege die Karte um den roten Pin auf deinen Standort zu setzen.',
    confirmLocation: 'Standort bestätigen',
    saved: 'Gespeichert',
    searchingLocation: 'Standort wird gesucht...',
    orderNumber: 'BESTELLUNG',
    client: 'Kunde',
    tel: 'Tel',
    delivery: 'Lieferung',
    payment: 'Zahlung',
    qty: 'ANZ',
    description: 'BESCHREIBUNG',
    total: 'GESAMT',
    thankYou: 'DANKE FÜR DEINE BESTELLUNG!',
    followUs: 'Folge uns in den sozialen Medien',
    usd: 'USD',
    edit: 'Bearbeiten',
    note: 'Notiz',
    addSomethingMore: 'Noch etwas hinzufügen',
  },
}

// Category name translations
export const categoryTranslations: Record<Language, Record<string, string>> = {
  es: {
    Todos: 'Todos',
    Sándwich: 'Sándwich',
    Wrap: 'Wrap',
    Cremas: 'Cremas',
    'Snack Bowl': 'Snack Bowl',
    Niños: 'Niños',
    Bebidas: 'Bebidas',
  },
  en: {
    Todos: 'All',
    Sándwich: 'Sandwich',
    Wrap: 'Wrap',
    Cremas: 'Soups',
    'Snack Bowl': 'Snack Bowl',
    Niños: 'Kids',
    Bebidas: 'Drinks',
  },
  fr: {
    Todos: 'Tous',
    Sándwich: 'Sandwich',
    Wrap: 'Wrap',
    Cremas: 'Soupes',
    'Snack Bowl': 'Snack Bowl',
    Niños: 'Enfants',
    Bebidas: 'Boissons',
  },
  zh: {
    Todos: '全部',
    Sándwich: '三明治',
    Wrap: '卷饼',
    Cremas: '浓汤',
    'Snack Bowl': '小食碗',
    Niños: '儿童',
    Bebidas: '饮品',
  },
  pt: {
    Todos: 'Todos',
    Sándwich: 'Sanduíche',
    Wrap: 'Wrap',
    Cremas: 'Sopas',
    'Snack Bowl': 'Snack Bowl',
    Niños: 'Crianças',
    Bebidas: 'Bebidas',
  },
  de: {
    Todos: 'Alle',
    Sándwich: 'Sandwich',
    Wrap: 'Wrap',
    Cremas: 'Suppen',
    'Snack Bowl': 'Snack Bowl',
    Niños: 'Kinder',
    Bebidas: 'Getränke',
  },
}

// Subcategory translations
export const subcategoryTranslations: Record<
  Language,
  Record<string, string>
> = {
  es: {
    'Naturales en Agua': 'Naturales en Agua',
    'Naturales en Leche': 'Naturales en Leche',
    Smoothies: 'Smoothies',
    Gaseosas: 'Gaseosas',
    Otras: 'Otras',
    Calientes: 'Calientes',
    Otros: 'Otros',
  },
  en: {
    'Naturales en Agua': 'Fresh Juices (Water)',
    'Naturales en Leche': 'Fresh Juices (Milk)',
    Smoothies: 'Smoothies',
    Gaseosas: 'Sodas',
    Otras: 'Other',
    Calientes: 'Hot Drinks',
    Otros: 'Other',
  },
  fr: {
    'Naturales en Agua': 'Jus naturels (eau)',
    'Naturales en Leche': 'Jus naturels (lait)',
    Smoothies: 'Smoothies',
    Gaseosas: 'Sodas',
    Otras: 'Autres',
    Calientes: 'Boissons chaudes',
    Otros: 'Autres',
  },
  zh: {
    'Naturales en Agua': '鲜榨果汁（水）',
    'Naturales en Leche': '鲜榨果汁（牛奶）',
    Smoothies: '冰沙',
    Gaseosas: '碳酸饮料',
    Otras: '其他',
    Calientes: '热饮',
    Otros: '其他',
  },
  pt: {
    'Naturales en Agua': 'Sucos Naturais (Água)',
    'Naturales en Leche': 'Sucos Naturais (Leite)',
    Smoothies: 'Smoothies',
    Gaseosas: 'Refrigerantes',
    Otras: 'Outras',
    Calientes: 'Bebidas Quentes',
    Otros: 'Outros',
  },
  de: {
    'Naturales en Agua': 'Natursäfte (Wasser)',
    'Naturales en Leche': 'Natursäfte (Milch)',
    Smoothies: 'Smoothies',
    Gaseosas: 'Limonaden',
    Otras: 'Andere',
    Calientes: 'Heißgetränke',
    Otros: 'Andere',
  },
}

// Product name & description translations (keyed by product ID)
export const productTranslations: Record<
  Language,
  Record<string, { name: string; description?: string }>
> = {
  es: {
    prod_sandwich: {
      name: 'Sándwich',
      description:
        'Armá tu sándwich en la barra, incluye 4 toppings, pan ciabatta, aderezo a elegir y chips',
    },
    prod_wrap: {
      name: 'Wrap',
      description:
        'Armá tu wrap en la barra, incluye 4 toppings, tortilla de trigo, aderezo a elegir y chips',
    },
    prod_crema_ayote: {
      name: 'Crema de Ayote',
      description: 'Acompañadas de crutones y queso mozzarella',
    },
    prod_crema_tomate: {
      name: 'Crema de Tomate',
      description: 'Acompañadas de crutones y queso mozzarella',
    },
    prod_snack_bowl: {
      name: 'Snack Bowl',
      description: 'Smoothie bowl del día con frutas y toppings a elegir',
    },
    prod_ninos_pollo: {
      name: 'Dedos de pollo',
      description:
        'Filetes de pollo ligeramente empanizados, servidos con chips y aderezo a elegir',
    },
    beb_nat_agua_fresa: { name: 'Natural en agua - Fresa' },
    beb_nat_agua_mora: { name: 'Natural en agua - Mora' },
    beb_nat_agua_pina: { name: 'Natural en agua - Piña' },
    beb_nat_agua_banano: { name: 'Natural en agua - Banano' },
    beb_nat_leche_fresa: { name: 'Natural en leche - Fresa' },
    beb_nat_leche_mora: { name: 'Natural en leche - Mora' },
    beb_nat_leche_pina: { name: 'Natural en leche - Piña' },
    beb_nat_leche_banano: { name: 'Natural en leche - Banano' },
    beb_smo_tropical: {
      name: 'Smoothie tropical',
      description: 'piña y fresa',
    },
    beb_smo_berry: {
      name: 'Smoothie Berry Mix',
      description: 'fresa, mora y arándanos',
    },
    beb_smo_alma: {
      name: 'Smoothie Nombre de tu negocio',
      description: 'espinaca, pepino, apio, piña y miel de abeja',
    },
    beb_gas_coca: { name: 'Coca Cola lata 237ml' },
    beb_gas_fanta: { name: 'Fanta naranja lata 237ml' },
    beb_gas_sprite: { name: 'Sprite lata 237ml' },
    beb_otr_agua_gas: { name: 'Agua con gas Sparkling 503ml' },
    beb_otr_tropical: { name: 'Tropical (variado) 250ml' },
    beb_otr_agua: { name: 'Botella de agua 500ml' },
    beb_cal_aguadulce: { name: 'Aguadulce en agua o leche' },
    beb_cal_chocolate: { name: 'Chocolate caliente en agua o leche' },
    custom_bowl: { name: 'Bowl Personalizado' },
  },
  en: {
    prod_sandwich: {
      name: 'Sandwich',
      description:
        'Build your sandwich at the bar, includes 4 toppings, ciabatta bread, dressing of your choice and chips',
    },
    prod_wrap: {
      name: 'Wrap',
      description:
        'Build your wrap at the bar, includes 4 toppings, wheat tortilla, dressing of your choice and chips',
    },
    prod_crema_ayote: {
      name: 'Squash Cream Soup',
      description: 'Served with croutons and mozzarella cheese',
    },
    prod_crema_tomate: {
      name: 'Tomato Cream Soup',
      description: 'Served with croutons and mozzarella cheese',
    },
    prod_snack_bowl: {
      name: 'Snack Bowl',
      description:
        'Daily smoothie bowl with fruits and toppings of your choice',
    },
    prod_ninos_pollo: {
      name: 'Chicken Fingers',
      description:
        'Lightly breaded chicken fillets, served with chips and dressing of your choice',
    },
    beb_nat_agua_fresa: { name: 'Fresh Juice (Water) - Strawberry' },
    beb_nat_agua_mora: { name: 'Fresh Juice (Water) - Blackberry' },
    beb_nat_agua_pina: { name: 'Fresh Juice (Water) - Pineapple' },
    beb_nat_agua_banano: { name: 'Fresh Juice (Water) - Banana' },
    beb_nat_leche_fresa: { name: 'Fresh Juice (Milk) - Strawberry' },
    beb_nat_leche_mora: { name: 'Fresh Juice (Milk) - Blackberry' },
    beb_nat_leche_pina: { name: 'Fresh Juice (Milk) - Pineapple' },
    beb_nat_leche_banano: { name: 'Fresh Juice (Milk) - Banana' },
    beb_smo_tropical: {
      name: 'Tropical Smoothie',
      description: 'pineapple and strawberry',
    },
    beb_smo_berry: {
      name: 'Berry Mix Smoothie',
      description: 'strawberry, blackberry and blueberries',
    },
    beb_smo_alma: {
      name: 'House Smoothie',
      description: 'spinach, cucumber, celery, pineapple and honey',
    },
    beb_gas_coca: { name: 'Coca Cola can 237ml' },
    beb_gas_fanta: { name: 'Fanta Orange can 237ml' },
    beb_gas_sprite: { name: 'Sprite can 237ml' },
    beb_otr_agua_gas: { name: 'Sparkling Water 503ml' },
    beb_otr_tropical: { name: 'Tropical (assorted) 250ml' },
    beb_otr_agua: { name: 'Water Bottle 500ml' },
    beb_cal_aguadulce: { name: 'Aguadulce (water or milk)' },
    beb_cal_chocolate: { name: 'Hot Chocolate (water or milk)' },
    custom_bowl: { name: 'Custom Bowl' },
  },
  fr: {
    prod_sandwich: {
      name: 'Sandwich',
      description:
        'Composez votre sandwich au bar, inclut 4 garnitures, pain ciabatta, sauce au choix et chips',
    },
    prod_wrap: {
      name: 'Wrap',
      description:
        'Composez votre wrap au bar, inclut 4 garnitures, tortilla de blé, sauce au choix et chips',
    },
    prod_crema_ayote: {
      name: 'Velouté de Courge',
      description: 'Accompagné de croûtons et mozzarella',
    },
    prod_crema_tomate: {
      name: 'Velouté de Tomate',
      description: 'Accompagné de croûtons et mozzarella',
    },
    prod_snack_bowl: {
      name: 'Snack Bowl',
      description: 'Smoothie bowl du jour avec fruits et garnitures au choix',
    },
    prod_ninos_pollo: {
      name: 'Bâtonnets de poulet',
      description:
        'Filets de poulet légèrement panés, servis avec chips et sauce au choix',
    },
    beb_nat_agua_fresa: { name: 'Jus naturel (eau) - Fraise' },
    beb_nat_agua_mora: { name: 'Jus naturel (eau) - Mûre' },
    beb_nat_agua_pina: { name: 'Jus naturel (eau) - Ananas' },
    beb_nat_agua_banano: { name: 'Jus naturel (eau) - Banane' },
    beb_nat_leche_fresa: { name: 'Jus naturel (lait) - Fraise' },
    beb_nat_leche_mora: { name: 'Jus naturel (lait) - Mûre' },
    beb_nat_leche_pina: { name: 'Jus naturel (lait) - Ananas' },
    beb_nat_leche_banano: { name: 'Jus naturel (lait) - Banane' },
    beb_smo_tropical: {
      name: 'Smoothie tropical',
      description: 'ananas et fraise',
    },
    beb_smo_berry: {
      name: 'Smoothie Berry Mix',
      description: 'fraise, mûre et myrtilles',
    },
    beb_smo_alma: {
      name: 'Smoothie maison',
      description: 'épinards, concombre, céleri, ananas et miel',
    },
    beb_gas_coca: { name: 'Coca Cola canette 237ml' },
    beb_gas_fanta: { name: 'Fanta orange canette 237ml' },
    beb_gas_sprite: { name: 'Sprite canette 237ml' },
    beb_otr_agua_gas: { name: 'Eau gazeuse 503ml' },
    beb_otr_tropical: { name: 'Tropical (varié) 250ml' },
    beb_otr_agua: { name: "Bouteille d'eau 500ml" },
    beb_cal_aguadulce: { name: 'Aguadulce (eau ou lait)' },
    beb_cal_chocolate: { name: 'Chocolat chaud (eau ou lait)' },
    custom_bowl: { name: 'Bowl personnalisé' },
  },
  zh: {
    prod_sandwich: {
      name: '三明治',
      description: '在吧台自选三明治，含4种配料、恰巴塔面包、酱料和薯片',
    },
    prod_wrap: {
      name: '卷饼',
      description: '在吧台自选卷饼，含4种配料、小麦饼皮、酱料和薯片',
    },
    prod_crema_ayote: {
      name: '南瓜浓汤',
      description: '配面包丁和马苏里拉奶酪',
    },
    prod_crema_tomate: {
      name: '番茄浓汤',
      description: '配面包丁和马苏里拉奶酪',
    },
    prod_snack_bowl: {
      name: '小食碗',
      description: '每日冰沙碗配水果和自选配料',
    },
    prod_ninos_pollo: {
      name: '鸡柳条',
      description: '轻裹面包屑的鸡肉条，配薯片和酱料',
    },
    beb_nat_agua_fresa: { name: '鲜榨果汁（水）- 草莓' },
    beb_nat_agua_mora: { name: '鲜榨果汁（水）- 黑莓' },
    beb_nat_agua_pina: { name: '鲜榨果汁（水）- 菠萝' },
    beb_nat_agua_banano: { name: '鲜榨果汁（水）- 香蕉' },
    beb_nat_leche_fresa: { name: '鲜榨果汁（牛奶）- 草莓' },
    beb_nat_leche_mora: { name: '鲜榨果汁（牛奶）- 黑莓' },
    beb_nat_leche_pina: { name: '鲜榨果汁（牛奶）- 菠萝' },
    beb_nat_leche_banano: { name: '鲜榨果汁（牛奶）- 香蕉' },
    beb_smo_tropical: { name: '热带冰沙', description: '菠萝和草莓' },
    beb_smo_berry: { name: '浆果混合冰沙', description: '草莓、黑莓和蓝莓' },
    beb_smo_alma: {
      name: '招牌冰沙',
      description: '菠菜、黄瓜、芹菜、菠萝和蜂蜜',
    },
    beb_gas_coca: { name: '可口可乐罐装 237ml' },
    beb_gas_fanta: { name: '芬达橙味罐装 237ml' },
    beb_gas_sprite: { name: '雪碧罐装 237ml' },
    beb_otr_agua_gas: { name: '气泡水 503ml' },
    beb_otr_tropical: { name: '热带饮料（混合）250ml' },
    beb_otr_agua: { name: '瓶装水 500ml' },
    beb_cal_aguadulce: { name: '甜水（水或牛奶）' },
    beb_cal_chocolate: { name: '热巧克力（水或牛奶）' },
    custom_bowl: { name: '自选碗' },
  },
  pt: {
    prod_sandwich: {
      name: 'Sanduíche',
      description:
        'Monte seu sanduíche no balcão, inclui 4 coberturas, pão ciabatta, molho à escolha e chips',
    },
    prod_wrap: {
      name: 'Wrap',
      description:
        'Monte seu wrap no balcão, inclui 4 coberturas, tortilha de trigo, molho à escolha e chips',
    },
    prod_crema_ayote: {
      name: 'Creme de Abóbora',
      description: 'Acompanhado de croutons e queijo mozzarella',
    },
    prod_crema_tomate: {
      name: 'Creme de Tomate',
      description: 'Acompanhado de croutons e queijo mozzarella',
    },
    prod_snack_bowl: {
      name: 'Snack Bowl',
      description: 'Smoothie bowl do dia com frutas e coberturas à escolha',
    },
    prod_ninos_pollo: {
      name: 'Tirinhas de Frango',
      description:
        'Filés de frango levemente empanados, servidos com chips e molho à escolha',
    },
    beb_nat_agua_fresa: { name: 'Suco natural (água) - Morango' },
    beb_nat_agua_mora: { name: 'Suco natural (água) - Amora' },
    beb_nat_agua_pina: { name: 'Suco natural (água) - Abacaxi' },
    beb_nat_agua_banano: { name: 'Suco natural (água) - Banana' },
    beb_nat_leche_fresa: { name: 'Suco natural (leite) - Morango' },
    beb_nat_leche_mora: { name: 'Suco natural (leite) - Amora' },
    beb_nat_leche_pina: { name: 'Suco natural (leite) - Abacaxi' },
    beb_nat_leche_banano: { name: 'Suco natural (leite) - Banana' },
    beb_smo_tropical: {
      name: 'Smoothie tropical',
      description: 'abacaxi e morango',
    },
    beb_smo_berry: {
      name: 'Smoothie Berry Mix',
      description: 'morango, amora e mirtilos',
    },
    beb_smo_alma: {
      name: 'Smoothie da casa',
      description: 'espinafre, pepino, aipo, abacaxi e mel',
    },
    beb_gas_coca: { name: 'Coca Cola lata 237ml' },
    beb_gas_fanta: { name: 'Fanta laranja lata 237ml' },
    beb_gas_sprite: { name: 'Sprite lata 237ml' },
    beb_otr_agua_gas: { name: 'Água com gás 503ml' },
    beb_otr_tropical: { name: 'Tropical (variado) 250ml' },
    beb_otr_agua: { name: 'Garrafa de água 500ml' },
    beb_cal_aguadulce: { name: 'Aguadulce (água ou leite)' },
    beb_cal_chocolate: { name: 'Chocolate quente (água ou leite)' },
    custom_bowl: { name: 'Bowl Personalizado' },
  },
  de: {
    prod_sandwich: {
      name: 'Sandwich',
      description:
        'Stelle dein Sandwich an der Bar zusammen, inkl. 4 Beläge, Ciabatta-Brot, Dressing nach Wahl und Chips',
    },
    prod_wrap: {
      name: 'Wrap',
      description:
        'Stelle deinen Wrap an der Bar zusammen, inkl. 4 Beläge, Weizentortilla, Dressing nach Wahl und Chips',
    },
    prod_crema_ayote: {
      name: 'Kürbiscremesuppe',
      description: 'Mit Croutons und Mozzarella',
    },
    prod_crema_tomate: {
      name: 'Tomatencremesuppe',
      description: 'Mit Croutons und Mozzarella',
    },
    prod_snack_bowl: {
      name: 'Snack Bowl',
      description: 'Tages-Smoothie-Bowl mit Früchten und Belägen nach Wahl',
    },
    prod_ninos_pollo: {
      name: 'Hähnchensticks',
      description:
        'Leicht panierte Hähnchenfilets, serviert mit Chips und Dressing nach Wahl',
    },
    beb_nat_agua_fresa: { name: 'Natursaft (Wasser) - Erdbeere' },
    beb_nat_agua_mora: { name: 'Natursaft (Wasser) - Brombeere' },
    beb_nat_agua_pina: { name: 'Natursaft (Wasser) - Ananas' },
    beb_nat_agua_banano: { name: 'Natursaft (Wasser) - Banane' },
    beb_nat_leche_fresa: { name: 'Natursaft (Milch) - Erdbeere' },
    beb_nat_leche_mora: { name: 'Natursaft (Milch) - Brombeere' },
    beb_nat_leche_pina: { name: 'Natursaft (Milch) - Ananas' },
    beb_nat_leche_banano: { name: 'Natursaft (Milch) - Banane' },
    beb_smo_tropical: {
      name: 'Tropischer Smoothie',
      description: 'Ananas und Erdbeere',
    },
    beb_smo_berry: {
      name: 'Berry Mix Smoothie',
      description: 'Erdbeere, Brombeere und Blaubeeren',
    },
    beb_smo_alma: {
      name: 'Haus-Smoothie',
      description: 'Spinat, Gurke, Sellerie, Ananas und Honig',
    },
    beb_gas_coca: { name: 'Coca Cola Dose 237ml' },
    beb_gas_fanta: { name: 'Fanta Orange Dose 237ml' },
    beb_gas_sprite: { name: 'Sprite Dose 237ml' },
    beb_otr_agua_gas: { name: 'Sprudelwasser 503ml' },
    beb_otr_tropical: { name: 'Tropical (gemischt) 250ml' },
    beb_otr_agua: { name: 'Wasserflasche 500ml' },
    beb_cal_aguadulce: { name: 'Aguadulce (Wasser oder Milch)' },
    beb_cal_chocolate: { name: 'Heiße Schokolade (Wasser oder Milch)' },
    custom_bowl: { name: 'Individuelle Bowl' },
  },
}

// Ingredient name translations (keyed by option ID)
export const ingredientTranslations: Record<
  Language,
  Record<string, string>
> = {
  es: {
    b1: 'Fideos de arroz',
    b2: 'Arroz blanco',
    b3: 'Lechuga',
    b4: 'Quinoa',
    b5: 'Pasta tipo pluma de garbanzos',
    b6: 'Mix de hojas verdes',
    p1: 'Salmón',
    p2: 'Atún',
    p3: 'Camarón',
    p4: 'Pollo',
    t1: 'Hongos (tarro)',
    t2: 'Huevo duro',
    t3: 'Cebolla crujiente',
    t4: 'Maíz dulce',
    t5: 'Cebolla morada',
    t6: 'Tomate cherry',
    t7: 'Frijoles negros',
    t8: 'Chile dulce',
    t9: 'Garbanzo',
    t10: 'Zanahoria',
    t11: 'Plátano maduro',
    t12: 'Cebollín',
    t13: 'Pepino',
    t14: 'Palmito',
    t15: 'Mango',
    t16: 'Fresa',
    t17: 'Piña',
    t18: 'Crutones',
    tp1: 'Aguacate',
    tp2: 'Pepinillos',
    tp3: 'Queso mozzarella',
    tp4: 'Edamames',
    tp5: 'Hongos naturales',
    a1: 'Aderezo de la casa',
    a2: 'Vinagreta balsámica',
    a3: 'Ranch',
    a4: 'Mostaza miel',
    a5: 'Aceite de oliva y limón',
    s1: 'Ajonjolí',
    s2: 'Semillas varias',
  },
  en: {
    b1: 'Rice Noodles',
    b2: 'White Rice',
    b3: 'Lettuce',
    b4: 'Quinoa',
    b5: 'Chickpea Penne Pasta',
    b6: 'Mixed Greens',
    p1: 'Salmon',
    p2: 'Tuna',
    p3: 'Shrimp',
    p4: 'Chicken',
    t1: 'Mushrooms (canned)',
    t2: 'Boiled Egg',
    t3: 'Crispy Onion',
    t4: 'Sweet Corn',
    t5: 'Red Onion',
    t6: 'Cherry Tomato',
    t7: 'Black Beans',
    t8: 'Sweet Pepper',
    t9: 'Chickpeas',
    t10: 'Carrot',
    t11: 'Sweet Plantain',
    t12: 'Scallion',
    t13: 'Cucumber',
    t14: 'Hearts of Palm',
    t15: 'Mango',
    t16: 'Strawberry',
    t17: 'Pineapple',
    t18: 'Croutons',
    tp1: 'Avocado',
    tp2: 'Pickles',
    tp3: 'Mozzarella Cheese',
    tp4: 'Edamame',
    tp5: 'Fresh Mushrooms',
    a1: 'House Dressing',
    a2: 'Balsamic Vinaigrette',
    a3: 'Ranch',
    a4: 'Honey Mustard',
    a5: 'Olive Oil & Lemon',
    s1: 'Sesame Seeds',
    s2: 'Mixed Seeds',
  },
  fr: {
    b1: 'Nouilles de riz',
    b2: 'Riz blanc',
    b3: 'Laitue',
    b4: 'Quinoa',
    b5: 'Pâtes penne aux pois chiches',
    b6: 'Mélange de verdures',
    p1: 'Saumon',
    p2: 'Thon',
    p3: 'Crevettes',
    p4: 'Poulet',
    t1: 'Champignons (conserve)',
    t2: 'Œuf dur',
    t3: 'Oignon croustillant',
    t4: 'Maïs doux',
    t5: 'Oignon rouge',
    t6: 'Tomate cerise',
    t7: 'Haricots noirs',
    t8: 'Poivron doux',
    t9: 'Pois chiches',
    t10: 'Carotte',
    t11: 'Banane plantain',
    t12: 'Ciboulette',
    t13: 'Concombre',
    t14: 'Cœurs de palmier',
    t15: 'Mangue',
    t16: 'Fraise',
    t17: 'Ananas',
    t18: 'Croûtons',
    tp1: 'Avocat',
    tp2: 'Cornichons',
    tp3: 'Mozzarella',
    tp4: 'Edamame',
    tp5: 'Champignons frais',
    a1: 'Sauce maison',
    a2: 'Vinaigrette balsamique',
    a3: 'Ranch',
    a4: 'Moutarde au miel',
    a5: "Huile d'olive et citron",
    s1: 'Graines de sésame',
    s2: 'Graines variées',
  },
  zh: {
    b1: '米粉',
    b2: '白米饭',
    b3: '生菜',
    b4: '藜麦',
    b5: '鹰嘴豆通心粉',
    b6: '混合绿叶菜',
    p1: '三文鱼',
    p2: '金枪鱼',
    p3: '虾',
    p4: '鸡肉',
    t1: '蘑菇（罐装）',
    t2: '水煮蛋',
    t3: '脆洋葱',
    t4: '甜玉米',
    t5: '紫洋葱',
    t6: '樱桃番茄',
    t7: '黑豆',
    t8: '甜椒',
    t9: '鹰嘴豆',
    t10: '胡萝卜',
    t11: '甜蕉',
    t12: '葱',
    t13: '黄瓜',
    t14: '棕榈心',
    t15: '芒果',
    t16: '草莓',
    t17: '菠萝',
    t18: '面包丁',
    tp1: '牛油果',
    tp2: '酸黄瓜',
    tp3: '马苏里拉奶酪',
    tp4: '毛豆',
    tp5: '新鲜蘑菇',
    a1: '招牌酱',
    a2: '意式黑醋汁',
    a3: '田园酱',
    a4: '蜂蜜芥末酱',
    a5: '橄榄油柠檬汁',
    s1: '芝麻',
    s2: '混合种子',
  },
  pt: {
    b1: 'Macarrão de arroz',
    b2: 'Arroz branco',
    b3: 'Alface',
    b4: 'Quinoa',
    b5: 'Massa penne de grão-de-bico',
    b6: 'Mix de folhas verdes',
    p1: 'Salmão',
    p2: 'Atum',
    p3: 'Camarão',
    p4: 'Frango',
    t1: 'Cogumelos (conserva)',
    t2: 'Ovo cozido',
    t3: 'Cebola crocante',
    t4: 'Milho doce',
    t5: 'Cebola roxa',
    t6: 'Tomate cereja',
    t7: 'Feijão preto',
    t8: 'Pimentão doce',
    t9: 'Grão-de-bico',
    t10: 'Cenoura',
    t11: 'Banana-da-terra',
    t12: 'Cebolinha',
    t13: 'Pepino',
    t14: 'Palmito',
    t15: 'Manga',
    t16: 'Morango',
    t17: 'Abacaxi',
    t18: 'Croutons',
    tp1: 'Abacate',
    tp2: 'Picles',
    tp3: 'Queijo mozzarella',
    tp4: 'Edamame',
    tp5: 'Cogumelos frescos',
    a1: 'Molho da casa',
    a2: 'Vinagrete balsâmico',
    a3: 'Ranch',
    a4: 'Mostarda com mel',
    a5: 'Azeite e limão',
    s1: 'Gergelim',
    s2: 'Sementes variadas',
  },
  de: {
    b1: 'Reisnudeln',
    b2: 'Weißer Reis',
    b3: 'Salat',
    b4: 'Quinoa',
    b5: 'Kichererbsen-Penne',
    b6: 'Gemischte Blätter',
    p1: 'Lachs',
    p2: 'Thunfisch',
    p3: 'Garnelen',
    p4: 'Hähnchen',
    t1: 'Pilze (Dose)',
    t2: 'Gekochtes Ei',
    t3: 'Knusprige Zwiebeln',
    t4: 'Süßer Mais',
    t5: 'Rote Zwiebel',
    t6: 'Kirschtomate',
    t7: 'Schwarze Bohnen',
    t8: 'Süße Paprika',
    t9: 'Kichererbsen',
    t10: 'Karotte',
    t11: 'Süße Kochbanane',
    t12: 'Frühlingszwiebel',
    t13: 'Gurke',
    t14: 'Palmherzen',
    t15: 'Mango',
    t16: 'Erdbeere',
    t17: 'Ananas',
    t18: 'Croutons',
    tp1: 'Avocado',
    tp2: 'Essiggurken',
    tp3: 'Mozzarella',
    tp4: 'Edamame',
    tp5: 'Frische Pilze',
    a1: 'Hausdressing',
    a2: 'Balsamico-Vinaigrette',
    a3: 'Ranch',
    a4: 'Honig-Senf',
    a5: 'Olivenöl & Zitrone',
    s1: 'Sesam',
    s2: 'Gemischte Samen',
  },
}

// Bowl builder step translations
export const bowlStepTranslations: Record<
  Language,
  Record<string, { title: string; subtitle: string; note?: string }>
> = {
  es: {
    base: { title: 'Base', subtitle: 'Elegí la base de tu bowl' },
    proteina: { title: 'Proteína', subtitle: 'Elegí tu proteína principal' },
    toppings: {
      title: 'Toppings',
      subtitle: 'Elegí hasta 4 toppings regulares',
    },
    toppingsPremium: {
      title: 'Premium',
      subtitle: 'Toppings especiales (con costo extra)',
    },
    aderezos: { title: 'Aderezos', subtitle: 'Elegí hasta 2 aderezos' },
    semillas: { title: 'Semillas', subtitle: 'El toque crujiente final' },
    comentarios: {
      title: 'Comentarios',
      subtitle: '¿Alguna instrucción especial?',
      note: 'Alergias o cambios específicos',
    },
  },
  en: {
    base: { title: 'Base', subtitle: 'Choose your bowl base' },
    proteina: { title: 'Protein', subtitle: 'Choose your main protein' },
    toppings: {
      title: 'Toppings',
      subtitle: 'Choose up to 4 regular toppings',
    },
    toppingsPremium: {
      title: 'Premium',
      subtitle: 'Special toppings (extra charge)',
    },
    aderezos: { title: 'Dressings', subtitle: 'Choose up to 2 dressings' },
    semillas: { title: 'Seeds', subtitle: 'The final crunchy touch' },
    comentarios: {
      title: 'Comments',
      subtitle: 'Any special instructions?',
      note: 'Allergies or specific changes',
    },
  },
  fr: {
    base: { title: 'Base', subtitle: 'Choisissez la base de votre bowl' },
    proteina: {
      title: 'Protéine',
      subtitle: 'Choisissez votre protéine principale',
    },
    toppings: {
      title: 'Garnitures',
      subtitle: "Choisissez jusqu'à 4 garnitures",
    },
    toppingsPremium: {
      title: 'Premium',
      subtitle: 'Garnitures spéciales (supplément)',
    },
    aderezos: { title: 'Sauces', subtitle: "Choisissez jusqu'à 2 sauces" },
    semillas: { title: 'Graines', subtitle: 'La touche croquante finale' },
    comentarios: {
      title: 'Commentaires',
      subtitle: 'Instructions spéciales ?',
      note: 'Allergies ou modifications spécifiques',
    },
  },
  zh: {
    base: { title: '底料', subtitle: '选择碗的底料' },
    proteina: { title: '蛋白质', subtitle: '选择主要蛋白质' },
    toppings: { title: '配料', subtitle: '选择最多4种常规配料' },
    toppingsPremium: { title: '高级配料', subtitle: '特色配料（额外收费）' },
    aderezos: { title: '酱料', subtitle: '选择最多2种酱料' },
    semillas: { title: '种子', subtitle: '最后的脆脆口感' },
    comentarios: {
      title: '备注',
      subtitle: '有特殊要求吗？',
      note: '过敏或特殊更改',
    },
  },
  pt: {
    base: { title: 'Base', subtitle: 'Escolha a base do seu bowl' },
    proteina: { title: 'Proteína', subtitle: 'Escolha sua proteína principal' },
    toppings: {
      title: 'Coberturas',
      subtitle: 'Escolha até 4 coberturas regulares',
    },
    toppingsPremium: {
      title: 'Premium',
      subtitle: 'Coberturas especiais (custo extra)',
    },
    aderezos: { title: 'Molhos', subtitle: 'Escolha até 2 molhos' },
    semillas: { title: 'Sementes', subtitle: 'O toque crocante final' },
    comentarios: {
      title: 'Comentários',
      subtitle: 'Instruções especiais?',
      note: 'Alergias ou alterações específicas',
    },
  },
  de: {
    base: { title: 'Basis', subtitle: 'Wähle die Basis deiner Bowl' },
    proteina: { title: 'Protein', subtitle: 'Wähle dein Hauptprotein' },
    toppings: { title: 'Beläge', subtitle: 'Wähle bis zu 4 reguläre Beläge' },
    toppingsPremium: {
      title: 'Premium',
      subtitle: 'Spezielle Beläge (Aufpreis)',
    },
    aderezos: { title: 'Dressings', subtitle: 'Wähle bis zu 2 Dressings' },
    semillas: { title: 'Samen', subtitle: 'Der knusprige letzte Schliff' },
    comentarios: {
      title: 'Kommentare',
      subtitle: 'Besondere Wünsche?',
      note: 'Allergien oder spezifische Änderungen',
    },
  },
}

// Labels for order ticket selection sections
export const selectionLabelTranslations: Record<
  Language,
  Record<string, string>
> = {
  es: {
    base: 'Base',
    proteina: 'Prot',
    toppings: 'Toppings',
    premium: 'Premium',
    dressing: 'Aderezo',
    seeds: 'Semillas',
  },
  en: {
    base: 'Base',
    proteina: 'Protein',
    toppings: 'Toppings',
    premium: 'Premium',
    dressing: 'Dressing',
    seeds: 'Seeds',
  },
  fr: {
    base: 'Base',
    proteina: 'Protéine',
    toppings: 'Garnitures',
    premium: 'Premium',
    dressing: 'Sauce',
    seeds: 'Graines',
  },
  zh: {
    base: '底料',
    proteina: '蛋白质',
    toppings: '配料',
    premium: '高级配料',
    dressing: '酱料',
    seeds: '种子',
  },
  pt: {
    base: 'Base',
    proteina: 'Proteína',
    toppings: 'Coberturas',
    premium: 'Premium',
    dressing: 'Molho',
    seeds: 'Sementes',
  },
  de: {
    base: 'Basis',
    proteina: 'Protein',
    toppings: 'Beläge',
    premium: 'Premium',
    dressing: 'Dressing',
    seeds: 'Samen',
  },
}
